import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { LanguageToggle, useT } from "../i18n";
import { ThemeToggle } from "../sirius/ThemeProvider";

/**
 * Plain-text legal page with Sirius nav + footer.
 * Used by both /terms and /privacy so they look identical.
 *
 * `sections[].bodyNb` / `bodyEn` contain trusted HTML strings (hard-coded by us
 * in Terms.jsx / Privacy.jsx, never user input). DOMPurify is still applied as
 * defense-in-depth so that any future copy edit or AI-generated content can't
 * accidentally introduce XSS via a forgotten sanitization step.
 *
 * Each section must carry a stable `id` (slug). We avoid array-index keys so
 * React can correctly track DOM nodes if the language toggle reorders content.
 */
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["a", "p", "br", "strong", "em", "ul", "ol", "li", "blockquote", "code", "span", "h3", "h4"],
  ALLOWED_ATTR: ["href", "style", "rel", "target", "id"],
};

export default function LegalLayout({ titleNb, titleEn, eyebrow, sections }) {
  const { lang } = useT();
  const title = lang === "en" && titleEn ? titleEn : titleNb;
  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="legal-root">
      <nav style={{ position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }}>
            <span className="srs-logo-dot" />
            <span className="srs-display" style={{ fontSize: 24, fontWeight: 500 }}>EuroPilot</span>
          </Link>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main style={{ flex: 1, maxWidth: 760, width: "100%", margin: "0 auto", padding: "60px 28px 80px" }}>
        <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 14 }}>{eyebrow}</div>
        <h1 className="srs-display" style={{ fontSize: 44, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 36px 0", lineHeight: 1.05 }}>
          {title}
        </h1>
        {sections.map((s, i) => {
          const heading = lang === "en" && s.titleEn ? s.titleEn : s.titleNb;
          const body = lang === "en" && s.bodyEn ? s.bodyEn : s.bodyNb;
          const safeHtml = DOMPurify.sanitize(body || "", PURIFY_CONFIG);
          // Prefer a stable id from the section; fall back to slugifying the
          // heading (still stable across renders), and only use index as a
          // last resort when both are missing.
          const key = s.id || (heading ? heading.toLowerCase().replace(/\s+/g, "-").slice(0, 40) : `s-${i}`);
          return (
            <section key={key} style={{ marginBottom: 32 }}>
              {heading && (
                <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 500, color: "var(--srs-ink)", margin: "0 0 12px 0", letterSpacing: "-0.01em" }}>
                  {heading}
                </h2>
              )}
              <div style={{ fontSize: 15, lineHeight: 1.7, color: "var(--srs-ink-dim)" }} dangerouslySetInnerHTML={{ __html: safeHtml }} />
            </section>
          );
        })}
      </main>

      <footer style={{ borderTop: "1px solid var(--srs-line)", padding: "24px 28px", textAlign: "center", fontSize: 12, color: "var(--srs-ink-faint)" }}>
        EuroPilot · Stavanger · <a href="mailto:hei@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>hei@europilot.no</a>
      </footer>
    </div>
  );
}
